<template>
  <div class="metricas-generales-dashboard">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 class="mb-0 font-weight-bold text-dark">
              <i class="fa fa-chart-line mr-2 text-primary"></i>
              Métricas Generales (Nuevo)
            </h3>
            <p class="text-muted mb-0">Visualización consolidada de métricas y estadísticas del sistema</p>
          </div>
          <div class="text-right">
            <small class="text-muted d-block">Última actualización</small>
            <small class="text-primary font-weight-bold">{{ new Date().toLocaleString('es-CL') }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Métricas de Membresías -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card shadow-sm border-0">
          <div class="card-header bg-white border-0">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">
                  <i class="fa fa-credit-card mr-2 text-primary"></i>
                  Membresías
                </h5>
                <p class="text-muted mb-0 mt-2" style="font-size: 0.875rem;">
                  Resumen general de membresías activas y distribución por estado de pago en el sistema.
                </p>
              </div>
              <button 
                class="btn btn-sm btn-outline-primary"
                @click="fetchMetricasMembresias"
                :disabled="loading.metricasMembresias"
              >
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading.metricasMembresias }"></i>
                <span class="ml-1">Actualizar</span>
              </button>
            </div>
          </div>
          
          <!-- Loading state -->
          <div v-if="loading.metricasMembresias" class="card-body text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
            <p class="text-muted">Cargando métricas de membresías...</p>
          </div>
          
          <!-- Contenido de métricas -->
          <div v-else class="card-body">
            <!-- KPIs Principales -->
            <div class="row mb-4">
              <!-- 1er Orden: ACTIVOS - Métrica Principal con énfasis -->
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stats-card stats-card-success stats-card-featured h-100">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="text-uppercase text-muted mb-2">
                          <i class="fa fa-star text-warning mr-1"></i>
                          Activos
                        </h6>
                        <h2 class="mb-0 font-weight-bold text-success">{{ metricasMembresias.general.activos.toLocaleString('es-CL') }}</h2>
                        <small class="text-success font-weight-bold">
                          <i class="fa fa-check-circle mr-1"></i>
                          {{ ((metricasMembresias.general.activos / metricasMembresias.general.totalMembresias) * 100).toFixed(1) }}% del total
                        </small>
                      </div>
                      <div class="icon-shape bg-success icon-shape-featured">
                        <i class="fa fa-user-check fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 2do Orden: VIGENTE -->
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stats-card stats-card-info h-100">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="text-uppercase text-muted mb-2">Vigentes</h6>
                        <h2 class="mb-0 font-weight-bold text-info">{{ metricasMembresias.general.vigente.toLocaleString('es-CL') }}</h2>
                        <small class="text-info">
                          <i class="fa fa-star mr-1"></i>
                          {{ ((metricasMembresias.general.vigente / metricasMembresias.general.totalMembresias) * 100).toFixed(1) }}%
                        </small>
                      </div>
                      <div class="icon-shape bg-info">
                        <i class="fa fa-star fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3er Orden: PAGADO -->
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stats-card stats-card-orange h-100">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="text-uppercase text-muted mb-2">Pagados</h6>
                        <h2 class="mb-0 font-weight-bold text-orange">{{ metricasMembresias.general.pagado.toLocaleString('es-CL') }}</h2>
                        <small class="text-orange">
                          <i class="fa fa-money-bill-wave mr-1"></i>
                          {{ ((metricasMembresias.general.pagado / metricasMembresias.general.totalMembresias) * 100).toFixed(1) }}%
                        </small>
                      </div>
                      <div class="icon-shape bg-orange">
                        <i class="fa fa-credit-card fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 4to Orden: CONGELADO -->
              <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stats-card stats-card-secondary h-100">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="text-uppercase text-muted mb-2">Congelados</h6>
                        <h2 class="mb-0 font-weight-bold text-secondary">{{ metricasMembresias.general.congelado.toLocaleString('es-CL') }}</h2>
                        <small class="text-secondary">
                          <i class="fa fa-pause-circle mr-1"></i>
                          {{ ((metricasMembresias.general.congelado / metricasMembresias.general.totalMembresias) * 100).toFixed(1) }}%
                        </small>
                      </div>
                      <div class="icon-shape bg-secondary">
                        <i class="fa fa-snowflake fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Indicadores adicionales (5to Orden: PENDIENTE y otros) -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="additional-metrics-box">
                  <div class="row align-items-center">
                    <div class="col-md-4 mb-2 mb-md-0">
                      <div class="d-flex align-items-center">
                        <i class="fa fa-clock text-warning fa-2x mr-3"></i>
                        <div>
                          <small class="text-muted d-block">Pendientes de Pago</small>
                          <h4 class="mb-0 font-weight-bold text-warning">{{ metricasMembresias.general.pendiente.toLocaleString('es-CL') }}</h4>
                          <small class="text-muted">{{ ((metricasMembresias.general.pendiente / metricasMembresias.general.totalMembresias) * 100).toFixed(1) }}% del total</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 mb-2 mb-md-0">
                      <div class="d-flex align-items-center">
                        <i class="fa fa-database text-primary fa-2x mr-3"></i>
                        <div>
                          <small class="text-muted d-block">Total Membresías</small>
                          <h4 class="mb-0 font-weight-bold text-primary">{{ metricasMembresias.general.totalMembresias.toLocaleString('es-CL') }}</h4>
                          <small class="text-muted">Registradas en el sistema</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="d-flex align-items-center">
                        <i class="fa fa-circle text-muted fa-2x mr-3"></i>
                        <div>
                          <small class="text-muted d-block">Otros Estados</small>
                          <h4 class="mb-0 font-weight-bold text-muted">{{ metricasMembresias.general.otro.toLocaleString('es-CL') }}</h4>
                          <small class="text-muted">{{ ((metricasMembresias.general.otro / metricasMembresias.general.totalMembresias) * 100).toFixed(1) }}% del total</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Distribución por Estado -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="font-weight-bold mb-3">
                  <i class="fa fa-chart-pie mr-2 text-primary"></i>
                  Distribución por Estado de Pago
                </h6>
                <div class="row">
                  <div 
                    v-for="estado in metricasMembresias.detalleEstados.filter(e => e.cantidad > 0)" 
                    :key="estado._id"
                    class="col-md-6 col-lg-4 mb-3"
                  >
                    <div class="estado-card">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0 font-weight-bold">{{ estado.glosa }}</h6>
                        <span class="badge" :class="getEstadoBadgeClass(estado.glosa)">
                          {{ estado.cantidad.toLocaleString('es-CL') }}
                        </span>
                      </div>
                      <div class="progress" style="height: 8px;">
                        <div 
                          class="progress-bar" 
                          :class="getEstadoProgressClass(estado.glosa)"
                          role="progressbar" 
                          :style="`width: ${(estado.cantidad / metricasMembresias.general.totalMembresias * 100).toFixed(1)}%`"
                        ></div>
                      </div>
                      <small class="text-muted">{{ estado.descripcion }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desglose por Sucursales -->
            <div v-if="metricasMembresias.sucursales && metricasMembresias.sucursales.length > 0" class="row">
              <div class="col-12">
                <h6 class="font-weight-bold mb-3">
                  <i class="fa fa-building mr-2 text-primary"></i>
                  Desglose por Sucursal
                  <span class="badge badge-primary ml-2">{{ metricasMembresias.sucursales.length }} sucursales</span>
                </h6>
                
                <div class="accordion" id="sucursalesAccordion">
                  <div 
                    v-for="(sucursal, index) in metricasMembresias.sucursales" 
                    :key="sucursal.sucursalId"
                    class="card sucursal-accordion-card mb-3"
                  >
                    <!-- Header de Sucursal (Colapsable) -->
                    <div 
                      class="card-header sucursal-accordion-header"
                      :id="`heading-${index}`"
                      @click="toggleSucursal(index)"
                      style="cursor: pointer;"
                    >
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                          <i 
                            class="fa mr-2 transition-icon" 
                            :class="sucursalExpandida === index ? 'fa-chevron-down' : 'fa-chevron-right'"
                          ></i>
                          <div>
                            <h6 class="mb-0 font-weight-bold">{{ sucursal.nombreSucursal }}</h6>
                            <small class="text-muted">
                              {{ sucursal.metricas.totalMembresias }} membresías totales · 
                              {{ sucursal.metricas.activos }} activos · 
                              {{ sucursal.metricas.vigente }} vigentes
                            </small>
                          </div>
                        </div>
                        <div class="d-flex align-items-center">
                          <span class="badge badge-success mr-2">
                            <i class="fa fa-star mr-1"></i>
                            {{ sucursal.metricas.activos }}
                          </span>
                          <span v-if="sucursal.tramoFacturacion" class="badge badge-primary">
                            Tramo {{ sucursal.tramoFacturacion.tramo }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Body de Sucursal (Contenido Colapsable) -->
                    <div 
                      :id="`collapse-${index}`"
                      class="collapse"
                      :class="{ 'show': sucursalExpandida === index }"
                      :aria-labelledby="`heading-${index}`"
                    >
                      <div class="card-body">
                        <!-- KPIs de la Sucursal (Orden actualizado) -->
                        <div class="row mb-3">
                          <!-- 1er Orden: ACTIVOS - Métrica Principal -->
                          <div class="col-md-3 col-6 mb-2">
                            <div class="sucursal-kpi-box sucursal-kpi-featured">
                              <small class="text-muted d-block font-weight-bold">
                                <i class="fa fa-star text-warning mr-1"></i>
                                Activos
                              </small>
                              <h5 class="mb-0 font-weight-bold text-success">{{ sucursal.metricas.activos }}</h5>
                            </div>
                          </div>
                          <!-- 2do Orden: VIGENTE -->
                          <div class="col-md-3 col-6 mb-2">
                            <div class="sucursal-kpi-box">
                              <small class="text-muted d-block">Vigentes</small>
                              <h5 class="mb-0 font-weight-bold text-info">{{ sucursal.metricas.vigente }}</h5>
                            </div>
                          </div>
                          <!-- 3er Orden: PAGADO -->
                          <div class="col-md-3 col-6 mb-2">
                            <div class="sucursal-kpi-box">
                              <small class="text-muted d-block">Pagados</small>
                              <h5 class="mb-0 font-weight-bold text-orange">{{ sucursal.metricas.pagado }}</h5>
                            </div>
                          </div>
                          <!-- 4to Orden: CONGELADO -->
                          <div class="col-md-3 col-6 mb-2">
                            <div class="sucursal-kpi-box">
                              <small class="text-muted d-block">Congelados</small>
                              <h5 class="mb-0 font-weight-bold text-secondary">{{ sucursal.metricas.congelado }}</h5>
                            </div>
                          </div>
                        </div>

                        <!-- Indicador de Pendientes (5to Orden) -->
                        <div class="row mb-3">
                          <div class="col-12">
                            <div class="sucursal-pendientes-box">
                              <div class="d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center">
                                  <i class="fa fa-clock text-warning fa-lg mr-2"></i>
                                  <div>
                                    <small class="text-muted d-block">Pendientes de Pago</small>
                                    <strong class="text-warning">{{ sucursal.metricas.pendiente }} membresías</strong>
                                  </div>
                                </div>
                                <div class="text-right">
                                  <small class="text-muted d-block">Total Membresías</small>
                                  <strong class="text-primary">{{ sucursal.metricas.totalMembresias }}</strong>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Estados con cantidad > 0 -->
                        <div class="row mb-3">
                          <div class="col-12">
                            <small class="text-muted font-weight-bold d-block mb-2">Estados Activos:</small>
                            <div class="d-flex flex-wrap" style="gap: 8px;">
                              <span 
                                v-for="estado in sucursal.detalleEstados.filter(e => e.cantidad > 0)" 
                                :key="estado._id"
                                class="badge badge-lg"
                                :class="getEstadoBadgeClass(estado.glosa)"
                              >
                                {{ estado.glosa }}: {{ estado.cantidad }}
                              </span>
                              <span 
                                v-if="sucursal.detalleEstados.filter(e => e.cantidad > 0).length === 0"
                                class="text-muted"
                              >
                                Sin membresías activas
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Tramo de Facturación de la Sucursal -->
                        <div v-if="sucursal.tramoFacturacion" class="row">
                          <div class="col-12">
                            <div class="tramo-sucursal-box">
                              <div class="row align-items-center">
                                <div class="col-lg-8 mb-3 mb-lg-0">
                                  <div class="d-flex align-items-center mb-2">
                                    <div class="tramo-badge-sm mr-2">
                                      Tramo {{ sucursal.tramoFacturacion.tramo }}
                                    </div>
                                    <div>
                                      <strong class="d-block">{{ sucursal.tramoFacturacion.nombreTramo }}</strong>
                                      <small class="text-muted">{{ sucursal.tramoFacturacion.descripcion }}</small>
                                    </div>
                                  </div>
                                  
                                  <div class="row text-center mt-3">
                                    <div class="col-4">
                                      <small class="text-muted d-block">Mínimo</small>
                                      <strong class="text-primary">{{ sucursal.tramoFacturacion.rangoMinimo }}</strong>
                                    </div>
                                    <div class="col-4">
                                      <small class="text-muted d-block">Activos</small>
                                      <strong class="text-success">{{ sucursal.tramoFacturacion.sociosActivos }}</strong>
                                    </div>
                                    <div class="col-4">
                                      <small class="text-muted d-block">Máximo</small>
                                      <strong class="text-info">{{ sucursal.tramoFacturacion.rangoMaximo }}</strong>
                                    </div>
                                  </div>
                                  
                                  <div class="mt-2">
                                    <div class="d-flex justify-content-between mb-1">
                                      <small class="text-muted">Ocupación</small>
                                      <small class="font-weight-bold text-primary">{{ sucursal.tramoFacturacion.porcentajeOcupacionTramo }}%</small>
                                    </div>
                                    <div class="progress" style="height: 6px;">
                                      <div 
                                        class="progress-bar bg-gradient-primary" 
                                        :style="`width: ${sucursal.tramoFacturacion.porcentajeOcupacionTramo}%`"
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div v-if="sucursal.tramoFacturacion.proximoTramo" class="col-lg-4">
                                  <div class="proximo-tramo-sm-box">
                                    <div class="d-flex align-items-center mb-1">
                                      <i class="fa fa-arrow-up text-success mr-2"></i>
                                      <strong class="small">{{ sucursal.tramoFacturacion.proximoTramo.nombreTramo }}</strong>
                                    </div>
                                    <div class="d-flex justify-content-between align-items-center">
                                      <small class="text-muted">Faltan</small>
                                      <h5 class="mb-0 font-weight-bold text-success">{{ sucursal.tramoFacturacion.proximoTramo.faltanSocios }}</h5>
                                    </div>
                                    <small class="text-muted">socios</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading.estadisticasSocios" class="row">
      <div class="col-12">
        <div class="card shadow-sm border-0">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
            <p class="text-muted">Cargando estadísticas de socios...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas de Socios -->
    <div v-else>
      <!-- Contenedor de Estadísticas de Socios -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0">
              <h5 class="mb-0">
                <i class="fa fa-users mr-2 text-primary"></i>
                Estadísticas de Socios
              </h5>
              <p class="text-muted mb-0 mt-2" style="font-size: 0.875rem;">
                Resumen estadístico de la base de socios, incluyendo distribución demográfica, 
                estados de cuenta, perfiles, membresías activas y credenciales de acceso configuradas.
              </p>
            </div>
            <div class="card-body">
              
      <!-- Tarjetas de Estadísticas Generales -->
      <div class="row mb-4">
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card stats-card stats-card-primary h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Total Socios</h6>
                  <h2 class="mb-0 font-weight-bold">{{ estadisticasSocios.general.totalSocios.toLocaleString('es-CL') }}</h2>
                  <small class="text-muted">
                    <i class="fa fa-users mr-1"></i>
                    Registrados en el sistema
                  </small>
                </div>
                <div class="icon-shape bg-primary">
                  <i class="fa fa-users fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card stats-card stats-card-success h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Nacionales</h6>
                  <h2 class="mb-0 font-weight-bold">{{ estadisticasSocios.general.totalNacionales.toLocaleString('es-CL') }}</h2>
                  <small class="text-success">
                    <i class="fa fa-arrow-up mr-1"></i>
                    {{ estadisticasSocios.general.porcentajeNacionales }}%
                  </small>
                </div>
                <div class="icon-shape bg-success">
                  <i class="fa fa-flag fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card stats-card stats-card-info h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Extranjeros</h6>
                  <h2 class="mb-0 font-weight-bold">{{ estadisticasSocios.general.totalExtranjeros.toLocaleString('es-CL') }}</h2>
                  <small class="text-info">
                    <i class="fa fa-globe mr-1"></i>
                    {{ estadisticasSocios.general.porcentajeExtranjeros }}%
                  </small>
                </div>
                <div class="icon-shape bg-info">
                  <i class="fa fa-globe-americas fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card stats-card stats-card-warning h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Con Membresía</h6>
                  <h2 class="mb-0 font-weight-bold">{{ (estadisticasSocios.planes.sociosConPlanPrincipal + estadisticasSocios.planes.sociosConPlanSecundario).toLocaleString('es-CL') }}</h2>
                  <small class="text-warning">
                    <i class="fa fa-credit-card mr-1"></i>
                    {{ (((estadisticasSocios.planes.sociosConPlanPrincipal + estadisticasSocios.planes.sociosConPlanSecundario) / estadisticasSocios.general.totalSocios) * 100).toFixed(1) }}% del total
                  </small>
                </div>
                <div class="icon-shape bg-warning">
                  <i class="fa fa-id-card fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sección de Distribución por Estado de Cuenta -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0">
              <h5 class="mb-0">
                <i class="fa fa-chart-pie mr-2 text-primary"></i>
                Distribución por Estado de Cuenta
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div 
                  v-for="estado in estadisticasSocios.distribucion.porEstadoCuenta" 
                  :key="estado._id"
                  class="col-md-6 col-lg-4 mb-3"
                >
                  <div class="estado-card">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="mb-0 font-weight-bold">{{ estado.glosa }}</h6>
                      <span class="badge" :class="getEstadoBadgeClass(estado.glosa)">
                        {{ estado.cantidad }}
                      </span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div 
                        class="progress-bar" 
                        :class="getEstadoProgressClass(estado.glosa)"
                        role="progressbar" 
                        :style="`width: ${(estado.cantidad / estadisticasSocios.general.totalSocios * 100).toFixed(1)}%`"
                      ></div>
                    </div>
                    <small class="text-muted">{{ estado.descripcion }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sección de Distribución por Perfil de Cuenta -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0">
              <h5 class="mb-0">
                <i class="fa fa-user-tag mr-2 text-primary"></i>
                Distribución por Perfil de Cuenta
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div 
                  v-for="perfil in estadisticasSocios.distribucion.porPerfilCuenta" 
                  :key="perfil._id"
                  class="col-md-6 col-lg-4 mb-3"
                >
                  <div class="perfil-card">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="mb-0 font-weight-bold">{{ perfil.glosa }}</h6>
                      <span class="badge badge-primary">{{ perfil.cantidad }}</span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div 
                        class="progress-bar bg-primary" 
                        role="progressbar" 
                        :style="`width: ${(perfil.cantidad / estadisticasSocios.general.totalSocios * 100).toFixed(1)}%`"
                      ></div>
                    </div>
                    <small class="text-muted">{{ perfil.descripcion }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sección de Planes y Valores -->
      <div class="row mb-4">
        <div class="col-lg-6 mb-3">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-header bg-white border-0">
              <h5 class="mb-0">
                <i class="fa fa-chart-line mr-2 text-primary"></i>
                Membresías
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Plan Principal</small>
                    <h3 class="mb-0 font-weight-bold text-success">{{ estadisticasSocios.planes.sociosConPlanPrincipal }}</h3>
                    <small class="text-success">{{ estadisticasSocios.planes.porcentajeConPlanPrincipal }}%</small>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Plan Secundario</small>
                    <h3 class="mb-0 font-weight-bold text-info">{{ estadisticasSocios.planes.sociosConPlanSecundario }}</h3>
                    <small class="text-info">{{ estadisticasSocios.planes.porcentajeConPlanSecundario }}%</small>
                  </div>
                </div>
                <div class="col-12 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Sin Membresía</small>
                    <h3 class="mb-0 font-weight-bold text-warning">{{ estadisticasSocios.planes.sociosSinPlan }}</h3>
                    <small class="text-warning">{{ estadisticasSocios.planes.porcentajeSinPlan }}%</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6 mb-3">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-header bg-white border-0">
              <h5 class="mb-0">
                <i class="fa fa-dollar-sign mr-2 text-primary"></i>
                Valores de Membresías
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Valor Total</small>
                    <h4 class="mb-0 font-weight-bold text-success">{{ estadisticasSocios.planes.valorTotalPlanesFormateado }}</h4>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Valor Promedio</small>
                    <h4 class="mb-0 font-weight-bold text-primary">{{ estadisticasSocios.planes.valorPromedioPlanesFormateado }}</h4>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Valor Mínimo</small>
                    <h5 class="mb-0 font-weight-bold text-secondary">{{ estadisticasSocios.planes.valorMinimoPlanFormateado }}</h5>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-box">
                    <small class="text-muted d-block mb-1">Valor Máximo</small>
                    <h5 class="mb-0 font-weight-bold text-secondary">{{ estadisticasSocios.planes.valorMaximoPlanFormateado }}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sección de Accesos y Credenciales -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0">
              <h5 class="mb-0">
                <i class="fa fa-key mr-2 text-primary"></i>
                Credenciales de Acceso
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div class="credential-card">
                    <div class="d-flex align-items-center mb-2">
                      <div class="credential-icon bg-warning text-white mr-3">
                        <i class="fa fa-key"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">Claves</h6>
                        <small class="text-muted">{{ estadisticasSocios.accesos.credenciales.sociosConClaves }} socios</small>
                      </div>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Total:</span>
                      <strong>{{ estadisticasSocios.accesos.credenciales.totalClaves }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Porcentaje:</span>
                      <strong>{{ estadisticasSocios.accesos.credenciales.porcentajeConClaves }}%</strong>
                    </div>
                    <div class="progress mt-2" style="height: 6px;">
                      <div 
                        class="progress-bar bg-warning" 
                        :style="`width: ${estadisticasSocios.accesos.credenciales.porcentajeConClaves}%`"
                      ></div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <div class="credential-card">
                    <div class="d-flex align-items-center mb-2">
                      <div class="credential-icon bg-info text-white mr-3">
                        <i class="fa fa-credit-card"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">Tarjetas</h6>
                        <small class="text-muted">{{ estadisticasSocios.accesos.credenciales.sociosConCredenciales }} socios</small>
                      </div>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Total:</span>
                      <strong>{{ estadisticasSocios.accesos.credenciales.totalCredenciales }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Porcentaje:</span>
                      <strong>{{ estadisticasSocios.accesos.credenciales.porcentajeConCredenciales }}%</strong>
                    </div>
                    <div class="progress mt-2" style="height: 6px;">
                      <div 
                        class="progress-bar bg-info" 
                        :style="`width: ${estadisticasSocios.accesos.credenciales.porcentajeConCredenciales}%`"
                      ></div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <div class="credential-card">
                    <div class="d-flex align-items-center mb-2">
                      <div class="credential-icon bg-success text-white mr-3">
                        <i class="fa fa-fingerprint"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">Biometrías</h6>
                        <small class="text-muted">{{ estadisticasSocios.accesos.credenciales.sociosConBiometrias }} socios</small>
                      </div>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Total:</span>
                      <strong>{{ estadisticasSocios.accesos.credenciales.totalBiometrias }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Porcentaje:</span>
                      <strong>{{ estadisticasSocios.accesos.credenciales.porcentajeConBiometrias }}%</strong>
                    </div>
                    <div class="progress mt-2" style="height: 6px;">
                      <div 
                        class="progress-bar bg-success" 
                        :style="`width: ${estadisticasSocios.accesos.credenciales.porcentajeConBiometrias}%`"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
              
            </div>
          </div>
        </div>
      </div>
      
      <!-- Sección de Valorización de Membresías -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h5 class="mb-0">
                    <i class="fa fa-coins mr-2 text-primary"></i>
                    Valorización de Cartera de Membresías
                  </h5>
                  <p class="text-muted mb-0 mt-2" style="font-size: 0.875rem;">
                    Análisis del valor económico de las membresías activas, mostrando valorización por plan, 
                    familia, subfamilia y estado de pago. Incluye filtros avanzados y paginación.
                  </p>
                </div>
                <button 
                  class="btn btn-sm btn-outline-primary"
                  @click="fetchValorizacionMembresias(1)"
                  :disabled="loading.valorizacionMembresias"
                >
                  <i class="fa fa-refresh" :class="{ 'fa-spin': loading.valorizacionMembresias }"></i>
                  <span class="ml-1">Actualizar</span>
                </button>
              </div>
            </div>
            
            <!-- Métricas Generales de Valorización -->
            <div class="card-body border-bottom">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div class="valorizacion-metric">
                    <i class="fa fa-users text-primary mr-2"></i>
                    <div>
                      <small class="text-muted d-block">Total Socios con Membresía</small>
                      <h4 class="mb-0 font-weight-bold">{{ valorizacionMembresias.metricas.totalGeneralSocios }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div class="valorizacion-metric">
                    <i class="fa fa-wallet text-success mr-2"></i>
                    <div>
                      <small class="text-muted d-block">Valor Total de Cartera</small>
                      <h4 class="mb-0 font-weight-bold text-success">{{ formatCurrency(valorizacionMembresias.metricas.valorTotalCartera) }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div class="valorizacion-metric">
                    <i class="fa fa-chart-line text-info mr-2"></i>
                    <div>
                      <small class="text-muted d-block">Valor Promedio General</small>
                      <h4 class="mb-0 font-weight-bold text-info">{{ formatCurrency(valorizacionMembresias.metricas.valorPromedioGeneral) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Filtros de Búsqueda -->
            <div class="card-body border-bottom bg-light">
              <div class="row align-items-end">
                <div class="col-md-3 mb-2">
                  <label class="form-label small text-muted mb-1">
                    <i class="fa fa-folder-open mr-1"></i>
                    Familia
                  </label>
                  <select 
                    class="form-control form-control-sm"
                    v-model="filtrosValorizacion.nombreFamilia"
                    @change="onFamiliaChange"
                  >
                    <option :value="null">Todas las familias</option>
                    <option 
                      v-for="familia in catalogosFiltros.familias" 
                      :key="familia.valor"
                      :value="familia.valor"
                    >
                      {{ familia.etiqueta }}
                    </option>
                  </select>
                </div>
                
                <div class="col-md-3 mb-2">
                  <label class="form-label small text-muted mb-1">
                    <i class="fa fa-folder mr-1"></i>
                    SubFamilia
                  </label>
                  <select 
                    class="form-control form-control-sm"
                    v-model="filtrosValorizacion.nombreSubFamilia"
                    @change="applyFilters"
                  >
                    <option :value="null">Todas las subfamilias</option>
                    <option 
                      v-for="subfamilia in catalogosFiltros.subfamilias" 
                      :key="subfamilia.valor"
                      :value="subfamilia.valor"
                    >
                      {{ subfamilia.etiqueta }}<span v-if="subfamilia.familia"> ({{ subfamilia.familia }})</span>
                    </option>
                  </select>
                </div>
                
                <div class="col-md-3 mb-2">
                  <label class="form-label small text-muted mb-1">
                    <i class="fa fa-info-circle mr-1"></i>
                    Estado de Pago
                  </label>
                  <select 
                    class="form-control form-control-sm"
                    v-model="filtrosValorizacion.estadoPago"
                    @change="applyFilters"
                  >
                    <option :value="null">Todos los estados</option>
                    <option 
                      v-for="estado in catalogosFiltros.estadosPago" 
                      :key="estado._id"
                      :value="estado._id"
                    >
                      {{ estado.glosa }}
                    </option>
                  </select>
                </div>
                
                <div class="col-md-3 mb-2">
                  <button 
                    class="btn btn-sm btn-outline-secondary btn-block"
                    @click="clearFilters"
                    :disabled="!filtrosValorizacion.nombreFamilia && !filtrosValorizacion.nombreSubFamilia && !filtrosValorizacion.estadoPago"
                  >
                    <i class="fa fa-times mr-1"></i>
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            </div>

            <!-- Tabla de Valorización -->
            <div class="card-body p-0">
              <div v-if="loading.valorizacionMembresias" class="text-center py-5">
                <div class="spinner-border text-primary mb-3" role="status">
                  <span class="sr-only">Cargando...</span>
                </div>
                <p class="text-muted">Cargando tabla de valorización...</p>
              </div>
              
              <div v-else class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="bg-light">
                    <tr>
                      <th class="border-0 text-uppercase text-muted small font-weight-bold">Plan / Membresía</th>
                      <th class="border-0 text-uppercase text-muted small font-weight-bold">Familia / SubFamilia</th>
                      <th class="border-0 text-uppercase text-muted small font-weight-bold text-center">Socios</th>
                      <th class="border-0 text-uppercase text-muted small font-weight-bold text-right">Valor Promedio</th>
                      <th class="border-0 text-uppercase text-muted small font-weight-bold text-right">Valor Total Cartera</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="valorizacionMembresias.membresias.length === 0">
                      <td colspan="5" class="text-center py-4 text-muted">
                        <i class="fa fa-inbox fa-2x mb-2"></i>
                        <p>No hay datos disponibles</p>
                      </td>
                    </tr>
                    <tr 
                      v-for="(item, index) in valorizacionMembresias.membresias" 
                      :key="index"
                      class="valorizacion-row"
                    >
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="plan-id-badge mr-2">{{ item.idPlan }}</div>
                          <div>
                            <div class="font-weight-bold text-dark">{{ item.descripcionPlan }}</div>
                            <small class="text-muted d-block" v-if="item.estadosPago && item.estadosPago.length > 0">
                              <span 
                                v-for="estado in item.estadosPago" 
                                :key="estado._id"
                                class="badge badge-sm mr-1"
                                :class="getEstadoBadgeClass(estado.glosa)"
                              >
                                {{ estado.glosa }}
                              </span>
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span class="badge badge-light mr-1">{{ item.nombreFamilia }}</span>
                          <span class="badge badge-secondary">{{ item.nombreSubFamilia }}</span>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="badge badge-primary badge-pill">{{ item.totalSocios }}</span>
                      </td>
                      <td class="text-right">
                        <strong>{{ formatCurrency(item.valorPromedio) }}</strong>
                        <small class="text-muted d-block" v-if="item.rangoPrecios">
                          Rango: {{ formatCurrency(item.valorMinimo) }} - {{ formatCurrency(item.valorMaximo) }}
                        </small>
                      </td>
                      <td class="text-right">
                        <strong class="text-success">{{ formatCurrency(item.valorTotal) }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Paginación -->
            <div class="card-footer bg-white border-0" v-if="valorizacionMembresias.pagination.totalPages > 1">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <small class="text-muted">
                    Página <strong>{{ valorizacionMembresias.pagination.page }}</strong> de <strong>{{ valorizacionMembresias.pagination.totalPages }}</strong>
                  </small>
                </div>
                <div class="d-flex align-items-center">
                  <button 
                    class="btn btn-sm btn-outline-secondary mr-2"
                    @click="handlePageChange(valorizacionMembresias.pagination.page - 1)"
                    :disabled="!valorizacionMembresias.pagination.hasPrev || loading.valorizacionMembresias"
                  >
                    <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="mx-2">{{ valorizacionMembresias.pagination.page }}</span>
                  <button 
                    class="btn btn-sm btn-outline-secondary ml-2"
                    @click="handlePageChange(valorizacionMembresias.pagination.page + 1)"
                    :disabled="!valorizacionMembresias.pagination.hasNext || loading.valorizacionMembresias"
                  >
                    <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
                <div>
                  <small class="text-muted">
                    Mostrando {{ valorizacionMembresias.membresias.length }} de {{ valorizacionMembresias.total }} planes
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Sección de Métricas de Ventas -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0 pb-0">
              <h5 class="mb-0">
                <i class="fa fa-cash-register mr-2 text-primary"></i>
                Métricas de Ventas de Sucursales
              </h5>
              <p class="text-muted mb-3 mt-2" style="font-size: 0.875rem;">
                KPIs y análisis de ventas realizadas en las sucursales, incluyendo distribución por estado, 
                sucursal, vendedor y categoría de productos. Filtrable por rango de fechas.
              </p>
              
              <!-- Filtros de Fecha -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                    <div class="d-flex align-items-center">
                      <label class="mb-0 mr-2 small text-muted" style="white-space: nowrap;">
                        <i class="fa fa-calendar-alt mr-1"></i>
                        Desde:
                      </label>
                      <el-date-picker
                        v-model="filtrosVentas.dateFromDate"
                        type="date"
                        placeholder="DD-MM-YYYY"
                        format="dd-MM-yyyy"
                        value-format="yyyy-MM-dd"
                        size="small"
                        editable
                        clearable
                        style="width: 150px;"
                        @change="onVentasDateFromChange"
                      />
                    </div>
                    <div class="d-flex align-items-center">
                      <label class="mb-0 mr-2 small text-muted" style="white-space: nowrap;">
                        <i class="fa fa-calendar-alt mr-1"></i>
                        Hasta:
                      </label>
                      <el-date-picker
                        v-model="filtrosVentas.dateToDate"
                        type="date"
                        placeholder="DD-MM-YYYY"
                        format="dd-MM-yyyy"
                        value-format="yyyy-MM-dd"
                        size="small"
                        editable
                        clearable
                        style="width: 150px;"
                        @change="onVentasDateToChange"
                      />
                    </div>
                    <button 
                      class="btn btn-sm btn-primary"
                      @click="applyDateFilters"
                      :disabled="loading.metricasVentas"
                    >
                      <i class="fa fa-search mr-1"></i>
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Loading state -->
            <div v-if="loading.metricasVentas" class="card-body text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
              <p class="text-muted">Cargando métricas de ventas...</p>
            </div>
            
            <!-- KPIs de Ventas -->
            <div v-else class="card-body">
              <div class="row mb-4">
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-primary">
                    <div class="kpi-icon bg-primary">
                      <i class="fa fa-shopping-cart"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Total Ventas</small>
                      <h3 class="mb-0 font-weight-bold">{{ metricasVentas.kpis.totalSales.toLocaleString('es-CL') }}</h3>
                      <small class="text-muted">{{ formatCurrency(metricasVentas.kpis.totalAmount) }}</small>
                    </div>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-success">
                    <div class="kpi-icon bg-success">
                      <i class="fa fa-check-circle"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Ventas Vigentes</small>
                      <h3 class="mb-0 font-weight-bold text-success">{{ metricasVentas.kpis.validSales.toLocaleString('es-CL') }}</h3>
                      <small class="text-success">{{ metricasVentas.kpis.validSalesPercentage }}% del total</small>
                    </div>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-danger">
                    <div class="kpi-icon bg-danger">
                      <i class="fa fa-times-circle"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Ventas Anuladas</small>
                      <h3 class="mb-0 font-weight-bold text-danger">{{ metricasVentas.kpis.cancelledSales.toLocaleString('es-CL') }}</h3>
                      <small class="text-muted">{{ (100 - metricasVentas.kpis.validSalesPercentage) }}% del total</small>
                    </div>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-info">
                    <div class="kpi-icon bg-info">
                      <i class="fa fa-calculator"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Venta Promedio</small>
                      <h3 class="mb-0 font-weight-bold text-info">{{ formatCurrency(metricasVentas.kpis.averageSaleAmount) }}</h3>
                      <small class="text-muted">Por transacción</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Distribuciones -->
              <div class="row">
                <!-- Ventas por Estado -->
                <div class="col-lg-6 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-chart-pie mr-2 text-primary"></i>
                      Distribución por Estado
                    </h6>
                    <div v-for="(item, index) in metricasVentas.charts.salesByStatus" :key="index" class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">{{ item.label }}</span>
                        <div class="text-right">
                          <span class="badge badge-primary mr-2">{{ item.value }}</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(item.amount) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar"
                          :class="item.label === '[VIGENTE]' ? 'bg-success' : 'bg-danger'"
                          :style="`width: ${(item.value / metricasVentas.kpis.totalSales * 100).toFixed(1)}%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Ventas por Sucursal (Top 5) -->
                <div class="col-lg-6 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-building mr-2 text-primary"></i>
                      Top 5 Sucursales
                    </h6>
                    <div v-for="(item, index) in metricasVentas.charts.salesByBranch.slice(0, 5)" :key="index" class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold text-truncate" style="max-width: 60%;">{{ item.label }}</span>
                        <div class="text-right">
                          <span class="badge badge-primary mr-2">{{ item.value }}</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(item.amount) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-info"
                          :style="`width: ${(item.value / metricasVentas.kpis.totalSales * 100).toFixed(1)}%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Ventas por Vendedor (Top 5) -->
                <div class="col-lg-6 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-user-tie mr-2 text-primary"></i>
                      Top 5 Vendedores
                    </h6>
                    <div v-for="(item, index) in metricasVentas.charts.salesBySeller.slice(0, 5)" :key="index" class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold text-truncate" style="max-width: 60%;">{{ item.label }}</span>
                        <div class="text-right">
                          <span class="badge badge-primary mr-2">{{ item.value }}</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(item.amount) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-warning"
                          :style="`width: ${(item.value / metricasVentas.kpis.totalSales * 100).toFixed(1)}%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Ventas por Categoría (Top 5) -->
                <div class="col-lg-6 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-tags mr-2 text-primary"></i>
                      Top 5 Categorías
                    </h6>
                    <div v-for="(item, index) in metricasVentas.charts.salesByCategory.slice(0, 5)" :key="index" class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">{{ item.label }}</span>
                        <div class="text-right">
                          <span class="badge badge-primary mr-2">{{ item.value }}</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(item.amount) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-purple"
                          :style="`width: ${(item.value / metricasVentas.kpis.totalSales * 100).toFixed(1)}%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Sección de Métricas de Cierres de Caja -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0 pb-0">
              <h5 class="mb-0">
                <i class="fa fa-calculator mr-2 text-primary"></i>
                Métricas de Cierres de Caja
              </h5>
              <p class="text-muted mb-3 mt-2" style="font-size: 0.875rem;">
                Análisis de cuadratura de cierres de caja, mostrando diferencias entre sistema y efectivo ratificado, 
                distribución por métodos de pago, sobrantes/faltantes y ranking de sucursales. Filtrable por período.
              </p>
              
              <!-- Filtros de Fecha -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                    <div class="d-flex align-items-center">
                      <label class="mb-0 mr-2 small text-muted" style="white-space: nowrap;">
                        <i class="fa fa-calendar-alt mr-1"></i>
                        Desde:
                      </label>
                      <el-date-picker
                        v-model="filtrosCierres.fechaInicioDate"
                        type="date"
                        placeholder="DD-MM-YYYY"
                        format="dd-MM-yyyy"
                        value-format="yyyy-MM-dd"
                        size="small"
                        editable
                        clearable
                        style="width: 150px;"
                        @change="onCierresDateInicioChange"
                      />
                    </div>
                    <div class="d-flex align-items-center">
                      <label class="mb-0 mr-2 small text-muted" style="white-space: nowrap;">
                        <i class="fa fa-calendar-alt mr-1"></i>
                        Hasta:
                      </label>
                      <el-date-picker
                        v-model="filtrosCierres.fechaFinDate"
                        type="date"
                        placeholder="DD-MM-YYYY"
                        format="dd-MM-yyyy"
                        value-format="yyyy-MM-dd"
                        size="small"
                        editable
                        clearable
                        style="width: 150px;"
                        @change="onCierresDateFinChange"
                      />
                    </div>
                    <button 
                      class="btn btn-sm btn-primary"
                      @click="applyDateFiltersCierres"
                      :disabled="loading.metricasCierres"
                    >
                      <i class="fa fa-search mr-1"></i>
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Loading state -->
            <div v-if="loading.metricasCierres" class="card-body text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
              <p class="text-muted">Cargando métricas de cierres de caja...</p>
            </div>
            
            <!-- KPIs de Cierres -->
            <div v-else class="card-body">
              <!-- Período consultado -->
              <div v-if="metricasCierres.periodo.descripcion" class="alert alert-info mb-4">
                <i class="fa fa-info-circle mr-2"></i>
                <strong>Período:</strong> {{ metricasCierres.periodo.descripcion }}
              </div>
              
              <!-- KPIs Principales -->
              <div class="row mb-4">
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-primary">
                    <div class="kpi-icon bg-primary">
                      <i class="fa fa-clipboard-list"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Total Cierres</small>
                      <h3 class="mb-0 font-weight-bold">{{ metricasCierres.metricas.totalCierres.toLocaleString('es-CL') }}</h3>
                      <small class="text-muted">Registros procesados</small>
                    </div>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-success">
                    <div class="kpi-icon bg-success">
                      <i class="fa fa-cash-register"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Total Sistema</small>
                      <h3 class="mb-0 font-weight-bold text-success">{{ formatCurrency(metricasCierres.metricas.totalVentasSistema) }}</h3>
                      <small class="text-muted">Ventas registradas</small>
                    </div>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-warning">
                    <div class="kpi-icon bg-warning">
                      <i class="fa fa-hand-holding-usd"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Total Ratificado</small>
                      <h3 class="mb-0 font-weight-bold text-warning">{{ formatCurrency(metricasCierres.metricas.totalVentasRatificadas) }}</h3>
                      <small class="text-muted">Efectivo contado</small>
                    </div>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="kpi-card kpi-card-danger">
                    <div class="kpi-icon bg-danger">
                      <i class="fa fa-exclamation-triangle"></i>
                    </div>
                    <div class="kpi-content">
                      <small class="text-muted text-uppercase">Total Diferencias</small>
                      <h3 class="mb-0 font-weight-bold text-danger">{{ formatCurrency(metricasCierres.metricas.totalDiferencias) }}</h3>
                      <small class="text-muted">Desviaciones detectadas</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Métricas de Diferencias -->
              <div class="row mb-4">
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="metric-box-cierre">
                    <i class="fa fa-chart-line text-info mb-2"></i>
                    <small class="text-muted d-block">Promedio Diferencia</small>
                    <h4 class="mb-0 font-weight-bold text-info">{{ formatCurrency(metricasCierres.metricas.promedioDiferencia) }}</h4>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="metric-box-cierre">
                    <i class="fa fa-exclamation-circle text-danger mb-2"></i>
                    <small class="text-muted d-block">Mayor Diferencia</small>
                    <h4 class="mb-0 font-weight-bold text-danger">{{ formatCurrency(metricasCierres.metricas.mayorDiferencia) }}</h4>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="metric-box-cierre">
                    <i class="fa fa-percentage text-warning mb-2"></i>
                    <small class="text-muted d-block">Cierres con Diferencias</small>
                    <h4 class="mb-0 font-weight-bold text-warning">{{ metricasCierres.metricas.porcentajeCierresConDiferencias }}%</h4>
                    <small class="text-muted">{{ metricasCierres.metricas.cierresConDiferencias }} de {{ metricasCierres.metricas.totalCierres }}</small>
                  </div>
                </div>
                
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="metric-box-cierre">
                    <i class="fa fa-wallet text-primary mb-2"></i>
                    <small class="text-muted d-block">Caja Inicial Total</small>
                    <h4 class="mb-0 font-weight-bold text-primary">{{ formatCurrency(metricasCierres.metricas.totalCajaInicial) }}</h4>
                  </div>
                </div>
              </div>
              
              <!-- Distribuciones -->
              <div class="row">
                <!-- Distribución por Método de Pago -->
                <div class="col-lg-6 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-credit-card mr-2 text-primary"></i>
                      Distribución por Método de Pago
                    </h6>
                    <div class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">💵 Efectivo</span>
                        <div class="text-right">
                          <span class="badge badge-success mr-2">{{ metricasCierres.metricas.porcentajesPorMetodoPago.efectivo }}%</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(metricasCierres.metricas.distribucionMetodosPago.efectivo) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-success"
                          :style="`width: ${metricasCierres.metricas.porcentajesPorMetodoPago.efectivo}%`"
                        ></div>
                      </div>
                    </div>
                    
                    <div class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">💳 Débito</span>
                        <div class="text-right">
                          <span class="badge badge-info mr-2">{{ metricasCierres.metricas.porcentajesPorMetodoPago.debito }}%</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(metricasCierres.metricas.distribucionMetodosPago.debito) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-info"
                          :style="`width: ${metricasCierres.metricas.porcentajesPorMetodoPago.debito}%`"
                        ></div>
                      </div>
                    </div>
                    
                    <div class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">💳 Crédito</span>
                        <div class="text-right">
                          <span class="badge badge-warning mr-2">{{ metricasCierres.metricas.porcentajesPorMetodoPago.credito }}%</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(metricasCierres.metricas.distribucionMetodosPago.credito) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-warning"
                          :style="`width: ${metricasCierres.metricas.porcentajesPorMetodoPago.credito}%`"
                        ></div>
                      </div>
                    </div>
                    
                    <div class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">📝 Cheque</span>
                        <div class="text-right">
                          <span class="badge badge-secondary mr-2">{{ metricasCierres.metricas.porcentajesPorMetodoPago.cheque }}%</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(metricasCierres.metricas.distribucionMetodosPago.cheque) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-secondary"
                          :style="`width: ${metricasCierres.metricas.porcentajesPorMetodoPago.cheque}%`"
                        ></div>
                      </div>
                    </div>
                    
                    <div class="distribution-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">🏦 Transferencia</span>
                        <div class="text-right">
                          <span class="badge badge-primary mr-2">{{ metricasCierres.metricas.porcentajesPorMetodoPago.transferencia }}%</span>
                          <span class="text-success font-weight-bold">{{ formatCurrency(metricasCierres.metricas.distribucionMetodosPago.transferencia) }}</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar bg-primary"
                          :style="`width: ${metricasCierres.metricas.porcentajesPorMetodoPago.transferencia}%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Sobrantes y Faltantes -->
                <div class="col-lg-6 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-balance-scale mr-2 text-primary"></i>
                      Sobrantes y Faltantes
                    </h6>
                    
                    <div class="sobrante-faltante-box mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <i class="fa fa-arrow-up text-success mr-2"></i>
                          <span class="font-weight-bold">Cierres con Sobrantes</span>
                        </div>
                        <span class="badge badge-success">{{ metricasCierres.metricas.cierresConSobrantes }}</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <small class="text-muted">Total Sobrantes</small>
                        <strong class="text-success">{{ formatCurrency(metricasCierres.metricas.totalSobrantes) }}</strong>
                      </div>
                      <div class="progress mt-2" style="height: 6px;">
                        <div 
                          class="progress-bar bg-success"
                          :style="`width: ${metricasCierres.metricas.porcentajeCierresConSobrantes}%`"
                        ></div>
                      </div>
                      <small class="text-muted">{{ metricasCierres.metricas.porcentajeCierresConSobrantes }}% del total</small>
                    </div>
                    
                    <div class="sobrante-faltante-box">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <i class="fa fa-arrow-down text-danger mr-2"></i>
                          <span class="font-weight-bold">Cierres con Faltantes</span>
                        </div>
                        <span class="badge badge-danger">{{ metricasCierres.metricas.cierresConFaltantes }}</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <small class="text-muted">Total Faltantes</small>
                        <strong class="text-danger">{{ formatCurrency(metricasCierres.metricas.totalFaltantes) }}</strong>
                      </div>
                      <div class="progress mt-2" style="height: 6px;">
                        <div 
                          class="progress-bar bg-danger"
                          :style="`width: ${metricasCierres.metricas.porcentajeCierresConFaltantes}%`"
                        ></div>
                      </div>
                      <small class="text-muted">{{ metricasCierres.metricas.porcentajeCierresConFaltantes }}% del total</small>
                    </div>
                  </div>
                </div>
                
                <!-- Top Sucursales -->
                <div v-if="metricasCierres.metricas.topSucursales && metricasCierres.metricas.topSucursales.length > 0" class="col-12 mb-4">
                  <div class="distribution-card">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fa fa-trophy mr-2 text-primary"></i>
                      Ranking de Sucursales
                    </h6>
                    <div class="row">
                      <div 
                        v-for="(sucursal, index) in metricasCierres.metricas.topSucursales" 
                        :key="index"
                        class="col-md-6 mb-3"
                      >
                        <div class="sucursal-ranking-card">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <div>
                              <span class="ranking-badge">#{{ index + 1 }}</span>
                              <strong class="ml-2">{{ sucursal.nombreSucursal }}</strong>
                            </div>
                            <span class="badge badge-primary">{{ sucursal.totalCierres }} cierres</span>
                          </div>
                          
                          <div class="row text-center mt-3">
                            <div class="col-6 border-right">
                              <small class="text-muted d-block">Total Ventas</small>
                              <strong class="text-success">{{ formatCurrency(sucursal.totalVentas) }}</strong>
                            </div>
                            <div class="col-6">
                              <small class="text-muted d-block">Promedio Diario</small>
                              <strong class="text-info">{{ formatCurrency(sucursal.promedioVentasDiarias) }}</strong>
                            </div>
                          </div>
                          
                          <div class="row text-center mt-2">
                            <div class="col-6 border-right">
                              <small class="text-muted d-block">Total Diferencias</small>
                              <strong class="text-danger">{{ formatCurrency(sucursal.totalDiferencias) }}</strong>
                            </div>
                            <div class="col-6">
                              <small class="text-muted d-block">Mayor Venta</small>
                              <strong class="text-warning">{{ formatCurrency(sucursal.mayorVentaDia) }}</strong>
                            </div>
                          </div>
                          
                          <div class="mt-2">
                            <small class="text-muted">Cierres con diferencias: {{ sucursal.porcentajeCierresConDiferencias.toFixed(2) }}%</small>
                            <div class="progress" style="height: 4px;">
                              <div 
                                class="progress-bar bg-warning"
                                :style="`width: ${sucursal.porcentajeCierresConDiferencias}%`"
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DatePicker } from "element-ui";

export default {
  name: 'MetricasGenerales',
  components: {
    [DatePicker.name]: DatePicker
  },
  data() {
    return {
      loading: {
        metricasMembresias: false,
        estadisticasSocios: false,
        valorizacionMembresias: false,
        metricasVentas: false,
        metricasCierres: false
      },
      // Datos de métricas de membresías
      metricasMembresias: {
        general: {
          activos: 0,
          vigente: 0,
          pagado: 0,
          congelado: 0,
          pendiente: 0,
          otro: 0,
          totalMembresias: 0
        },
        detalleEstados: [],
        sucursales: [],
        metadata: {}
      },
      // Control de expansión de sucursales
      sucursalExpandida: null,
      // Datos de estadísticas de socios
      estadisticasSocios: {
        general: {
          totalSocios: 0,
          totalExtranjeros: 0,
          totalNacionales: 0,
          porcentajeExtranjeros: "0",
          porcentajeNacionales: "0"
        },
        distribucion: {
          porEstadoCuenta: [],
          porPerfilCuenta: [],
          porGenero: [],
          geografica: [],
          porEdad: []
        },
        planes: {
          sociosConPlanPrincipal: 0,
          sociosConPlanSecundario: 0,
          sociosSinPlan: 0,
          valorPromedioPlanes: 0,
          valorTotalPlanes: 0,
          valorMinimoPlan: 0,
          valorMaximoPlan: 0,
          porcentajeConPlanPrincipal: "0",
          porcentajeConPlanSecundario: "0",
          porcentajeSinPlan: "0",
          valorPromedioPlanesFormateado: "$0",
          valorTotalPlanesFormateado: "$0",
          valorMinimoPlanFormateado: "$0",
          valorMaximoPlanFormateado: "$0"
        },
        accesos: {
          porEstado: [],
          credenciales: {
            sociosConClaves: 0,
            totalClaves: 0,
            sociosConCredenciales: 0,
            totalCredenciales: 0,
            sociosConBiometrias: 0,
            totalBiometrias: 0,
            porcentajeConClaves: "0",
            porcentajeConCredenciales: "0",
            porcentajeConBiometrias: "0",
            promedioClavesPorSocio: "0",
            promedioCredencialesPorSocio: "0",
            promedioBiometriasPorSocio: "0"
          }
        }
      },
      // Filtros opcionales (mismos que consulta paginada)
      filtros: {
        sucursal: null,
        search: null,
        estadoCuenta: null,
        estadoAcceso: null,
        perfilCuenta: null
      },
      // Datos de valorización de membresías
      valorizacionMembresias: {
        membresias: [],
        total: 0,
        pagination: {
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        },
        metricas: {
          totalGeneralSocios: 0,
          valorTotalCartera: 0,
          valorPromedioGeneral: 0
        }
      },
      // Filtros para valorización
      filtrosValorizacion: {
        nombreFamilia: null,
        nombreSubFamilia: null,
        estadoPago: null
      },
      // Catálogos para filtros de valorización
      catalogosFiltros: {
        familias: [],
        subfamilias: [],
        estadosPago: []
      },
      // Datos de métricas de ventas
      metricasVentas: {
        kpis: {
          totalSales: 0,
          totalAmount: 0,
          validSales: 0,
          cancelledSales: 0,
          closedSales: 0,
          averageSaleAmount: 0,
          validSalesPercentage: 0
        },
        charts: {
          salesByStatus: [],
          salesByBranch: [],
          salesBySeller: [],
          salesByCategory: [],
          salesByCashier: []
        },
        meta: {
          filtersApplied: {},
          dataWindow: {},
          totals: {
            recordsConsidered: 0,
            currency: 'CLP'
          }
        }
      },
      // Filtros de ventas
      filtrosVentas: {
        dateFrom: '',
        dateTo: '',
        dateFromDate: null,
        dateToDate: null
      },
      // Datos de métricas de cierres de caja
      metricasCierres: {
        metricas: {
          totalCierres: 0,
          totalVentasEfectivo: 0,
          totalVentasTDebito: 0,
          totalVentasTCredito: 0,
          totalVentasCheque: 0,
          totalVentasTransferencia: 0,
          totalVentasCreditoLocal: 0,
          totalVentasSistema: 0,
          totalVentasRatificadas: 0,
          totalCajaInicial: 0,
          totalDiferencias: 0,
          promedioDiferencia: 0,
          sumaDiferenciasAbsolutas: 0,
          mayorDiferencia: 0,
          cierresConDiferencias: 0,
          cierresConSobrantes: 0,
          cierresConFaltantes: 0,
          totalSobrantes: 0,
          totalFaltantes: 0,
          porcentajeCierresConDiferencias: '0',
          porcentajeCierresConSobrantes: '0',
          porcentajeCierresConFaltantes: '0',
          distribucionMetodosPago: {},
          porcentajesPorMetodoPago: {},
          topSucursales: []
        },
        periodo: {},
        filtrosAplicados: {}
      },
      // Filtros de cierres
      filtrosCierres: {
        fechaInicio: '',
        fechaFin: '',
        fechaInicioDate: null,
        fechaFinDate: null
      }
    };
  },
  methods: {
    async fetchMetricasMembresias() {
      this.loading.metricasMembresias = true;
      
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/membresias/principales/metricas-estados"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          const dataOut = response.data.dataOut;
          this.metricasMembresias = {
            general: dataOut.general || this.metricasMembresias.general,
            detalleEstados: dataOut.detalleEstados || [],
            sucursales: dataOut.sucursales || [],
            metadata: dataOut.metadata || {}
          };
        } else {
          throw new Error(response.data.result.message || 'Error al obtener métricas de membresías');
        }
      } catch (error) {
        console.error('Error al cargar métricas de membresías:', error);
        this.$swal.fire({
          title: 'Error',
          text: error.message || 'No se pudieron cargar las métricas de membresías',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading.metricasMembresias = false;
      }
    },
    
    async fetchEstadisticasSocios() {
      this.loading.estadisticasSocios = true;
      
      try {
        // Preparar parameters con filtros opcionales
        const parameters = {};
        if (this.filtros.sucursal) parameters.sucursal = this.filtros.sucursal;
        if (this.filtros.search) parameters.search = this.filtros.search;
        if (this.filtros.estadoCuenta) parameters.estadoCuenta = this.filtros.estadoCuenta;
        if (this.filtros.estadoAcceso) parameters.estadoAcceso = this.filtros.estadoAcceso;
        if (this.filtros.perfilCuenta) parameters.perfilCuenta = this.filtros.perfilCuenta;
        
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/socios/estadisticas"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          const dataOut = response.data.dataOut;
          this.estadisticasSocios = {
            general: dataOut.general || this.estadisticasSocios.general,
            distribucion: dataOut.distribucion || this.estadisticasSocios.distribucion,
            planes: dataOut.planes || this.estadisticasSocios.planes,
            accesos: dataOut.accesos || this.estadisticasSocios.accesos
          };
        } else {
          throw new Error(response.data.result.message || 'Error al obtener estadísticas');
        }
      } catch (error) {
        console.error('Error al cargar estadísticas de socios:', error);
        this.$swal.fire({
          title: 'Error',
          text: error.message || 'No se pudieron cargar las estadísticas de socios',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading.estadisticasSocios = false;
      }
    },
    
    getEstadoBadgeClass(glosa) {
      const clases = {
        // Estados de cuenta (socios)
        'ACTIVA': 'badge-success',
        'INACTIVA': 'badge-secondary',
        'BLOQUEADA': 'badge-danger',
        'ELIMINADA': 'badge-dark',
        // Estados de pago (membresías)
        'VIGENTE': 'badge-success',
        'PENDIENTE': 'badge-warning',
        'R:PENDIENTE': 'badge-warning',
        'PAGADO': 'badge-orange',
        'FINALIZADO': 'badge-secondary',
        'CONGELADO': 'badge-info'
      };
      return clases[glosa] || 'badge-secondary';
    },
    
    getEstadoProgressClass(glosa) {
      const clases = {
        // Estados de cuenta (socios)
        'ACTIVA': 'bg-success',
        'INACTIVA': 'bg-secondary',
        'BLOQUEADA': 'bg-danger',
        'ELIMINADA': 'bg-dark',
        // Estados de pago (membresías)
        'VIGENTE': 'bg-success',
        'PENDIENTE': 'bg-warning',
        'R:PENDIENTE': 'bg-warning',
        'PAGADO': 'bg-warning',
        'FINALIZADO': 'bg-secondary',
        'CONGELADO': 'bg-info'
      };
      return clases[glosa] || 'bg-secondary';
    },
    
    async fetchValorizacionMembresias(page = 1) {
      this.loading.valorizacionMembresias = true;
      
      try {
        const parameters = {
          page: page,
          limit: this.valorizacionMembresias.pagination.limit
        };
        
        // Agregar filtros opcionales
        if (this.filtrosValorizacion.nombreFamilia) {
          parameters.nombreFamilia = this.filtrosValorizacion.nombreFamilia;
        }
        if (this.filtrosValorizacion.nombreSubFamilia) {
          parameters.nombreSubFamilia = this.filtrosValorizacion.nombreSubFamilia;
        }
        if (this.filtrosValorizacion.estadoPago) {
          parameters.estadoPago = this.filtrosValorizacion.estadoPago;
        }
        
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/membresias/principales/metricas-valorizacion"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          const dataOut = response.data.dataOut;
          this.valorizacionMembresias = {
            membresias: dataOut.membresias || [],
            total: dataOut.total || 0,
            pagination: dataOut.pagination || this.valorizacionMembresias.pagination,
            metricas: dataOut.metricas || this.valorizacionMembresias.metricas
          };
        } else {
          throw new Error(response.data.result.message || 'Error al obtener valorización');
        }
      } catch (error) {
        console.error('Error al cargar valorización de membresías:', error);
        this.$swal.fire({
          title: 'Error',
          text: error.message || 'No se pudieron cargar las métricas de valorización',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading.valorizacionMembresias = false;
      }
    },
    
    handlePageChange(page) {
      this.fetchValorizacionMembresias(page);
    },
    
    formatCurrency(value) {
      if (!value && value !== 0) return '$0';
      return '$' + new Intl.NumberFormat('es-CL').format(value);
    },
    
    async fetchCatalogoFamilias() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/membresias/principales/catalogo-familias"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          this.catalogosFiltros.familias = response.data.dataOut.catalogo || [];
        }
      } catch (error) {
        console.error('Error al cargar catálogo de familias:', error);
      }
    },
    
    async fetchCatalogoSubFamilias(nombreFamilia = null) {
      try {
        const parameters = {};
        
        // Agregar filtro por familia si está seleccionada
        if (nombreFamilia) {
          parameters.nombreFamilia = nombreFamilia;
        }
        
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/membresias/principales/catalogo-subfamilias"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          this.catalogosFiltros.subfamilias = response.data.dataOut.catalogo || [];
        }
      } catch (error) {
        console.error('Error al cargar catálogo de subfamilias:', error);
      }
    },
    
    async fetchCatalogoEstadosPago() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/membresias/catalogo"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          this.catalogosFiltros.estadosPago = response.data.dataOut.estadosPago || [];
        }
      } catch (error) {
        console.error('Error al cargar catálogo de estados de pago:', error);
      }
    },
    
    async onFamiliaChange() {
      // Limpiar subfamilia seleccionada cuando cambia la familia
      this.filtrosValorizacion.nombreSubFamilia = null;
      
      // Recargar catálogo de subfamilias filtrado por la familia seleccionada
      await this.fetchCatalogoSubFamilias(this.filtrosValorizacion.nombreFamilia);
      
      // Aplicar filtros
      this.applyFilters();
    },
    
    applyFilters() {
      // Recargar la tabla con los filtros aplicados desde página 1
      this.fetchValorizacionMembresias(1);
    },
    
    async clearFilters() {
      this.filtrosValorizacion = {
        nombreFamilia: null,
        nombreSubFamilia: null,
        estadoPago: null
      };
      
      // Recargar catálogo completo de subfamilias
      await this.fetchCatalogoSubFamilias();
      
      // Recargar tabla sin filtros
      this.fetchValorizacionMembresias(1);
    },
    
    // Métodos para métricas de ventas
    initializeDateFilters() {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      // Filtros para ventas (formato DD-MM-YYYY)
      this.filtrosVentas.dateFrom = this.formatDateToDDMMYYYY(firstDay);
      this.filtrosVentas.dateTo = this.formatDateToDDMMYYYY(lastDay);
      this.filtrosVentas.dateFromDate = new Date(firstDay);
      this.filtrosVentas.dateToDate = new Date(lastDay);
      
      // Filtros para cierres (formato YYYY-MM-DD)
      this.filtrosCierres.fechaInicio = this.formatDateToYYYYMMDD(firstDay);
      this.filtrosCierres.fechaFin = this.formatDateToYYYYMMDD(lastDay);
      this.filtrosCierres.fechaInicioDate = new Date(firstDay);
      this.filtrosCierres.fechaFinDate = new Date(lastDay);
    },
    
    formatDateToDDMMYYYY(date) {
      if (!date) return '';
      // Si date es un string en formato YYYY-MM-DD, convertir a Date
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}-${month}-${year}`;
    },
    
    formatDateToYYYYMMDD(date) {
      if (!date) return '';
      // Si date es un string en formato YYYY-MM-DD, devolverlo tal cual
      if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date;
      }
      // Si es un Date, convertir
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${year}-${month}-${day}`;
    },
    
    onVentasDateFromChange(date) {
      if (date) {
        // El value-format="yyyy-MM-dd" devuelve un string YYYY-MM-DD
        // Convertir a DD-MM-YYYY para el endpoint de ventas
        this.filtrosVentas.dateFrom = this.formatDateToDDMMYYYY(date);
      }
    },
    
    onVentasDateToChange(date) {
      if (date) {
        // El value-format="yyyy-MM-dd" devuelve un string YYYY-MM-DD
        // Convertir a DD-MM-YYYY para el endpoint de ventas
        this.filtrosVentas.dateTo = this.formatDateToDDMMYYYY(date);
      }
    },
    
    onCierresDateInicioChange(date) {
      if (date) {
        // El value-format="yyyy-MM-dd" devuelve un string YYYY-MM-DD
        // Usar directamente para el endpoint de cierres (ya está en el formato correcto)
        this.filtrosCierres.fechaInicio = this.formatDateToYYYYMMDD(date);
      }
    },
    
    onCierresDateFinChange(date) {
      if (date) {
        // El value-format="yyyy-MM-dd" devuelve un string YYYY-MM-DD
        // Usar directamente para el endpoint de cierres (ya está en el formato correcto)
        this.filtrosCierres.fechaFin = this.formatDateToYYYYMMDD(date);
      }
    },
    
    async fetchMetricasVentas() {
      this.loading.metricasVentas = true;
      
      try {
        const parameters = {
          dateFrom: this.filtrosVentas.dateFrom,
          dateTo: this.filtrosVentas.dateTo
        };
        
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/sales/get/gymsales/metrics"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });
        
        if (response.data.result && response.data.result.kpis) {
          this.metricasVentas = {
            kpis: response.data.result.kpis || this.metricasVentas.kpis,
            charts: response.data.result.charts || this.metricasVentas.charts,
            meta: response.data.result.meta || this.metricasVentas.meta
          };
        } else {
          throw new Error('Error al obtener métricas de ventas');
        }
      } catch (error) {
        console.error('Error al cargar métricas de ventas:', error);
        this.$swal.fire({
          title: 'Error',
          text: error.message || 'No se pudieron cargar las métricas de ventas',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading.metricasVentas = false;
      }
    },
    
    applyDateFilters() {
      this.fetchMetricasVentas();
    },
    
    // Métodos para métricas de cierres de caja
    async fetchMetricasCierres() {
      this.loading.metricasCierres = true;
      
      try {
        const parameters = {
          fechaInicio: this.filtrosCierres.fechaInicio,
          fechaFin: this.filtrosCierres.fechaFin
        };
        
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/cierres-caja/metrics"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });
        
        if (response.data.Response && response.data.Response.Data) {
          const data = response.data.Response.Data;
          this.metricasCierres = {
            metricas: data.metricas || this.metricasCierres.metricas,
            periodo: data.periodo || this.metricasCierres.periodo,
            filtrosAplicados: data.filtrosAplicados || this.metricasCierres.filtrosAplicados
          };
        } else {
          throw new Error('Error al obtener métricas de cierres de caja');
        }
      } catch (error) {
        console.error('Error al cargar métricas de cierres:', error);
        this.$swal.fire({
          title: 'Error',
          text: error.message || 'No se pudieron cargar las métricas de cierres de caja',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading.metricasCierres = false;
      }
    },
    
    applyDateFiltersCierres() {
      this.fetchMetricasCierres();
    },
    
    toggleSucursal(index) {
      // Si se hace clic en la misma sucursal, colapsar; si no, expandir la nueva
      this.sucursalExpandida = this.sucursalExpandida === index ? null : index;
    }
  },
  async mounted() {
    // Minimizar sidebar si está expandido
    if (this.$sidebar.isMinimized) {
      this.$sidebar.toggleMinimize();
    }
    
    // Inicializar filtros de fecha para ventas (mes actual)
    this.initializeDateFilters();
    
    // Cargar catálogos primero (subfamilias sin filtro para cargar todas)
    await Promise.all([
      this.fetchCatalogoFamilias(),
      this.fetchCatalogoSubFamilias(null),
      this.fetchCatalogoEstadosPago()
    ]);
    
    // Cargar métricas de membresías, estadísticas de socios, valorización de membresías, métricas de ventas y cierres
    await Promise.all([
      this.fetchMetricasMembresias(),
      this.fetchEstadisticasSocios(),
      this.fetchValorizacionMembresias(),
      this.fetchMetricasVentas(),
      this.fetchMetricasCierres()
    ]);
  }
};
</script>

<style scoped>
.metricas-generales-dashboard {
  width: 100%;
}

/* Header styles */
h3 {
  color: #2c3e50;
}

.text-primary {
  color: #667eea !important;
}

/* Tarjetas de estadísticas */
.stats-card {
  border: none;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0.75rem 1.5rem rgba(0,0,0,0.12) !important;
}

.stats-card h2 {
  font-size: clamp(0.8rem, 2vw, 2rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  line-height: 1.2;
  max-width: 100%;
}

.stats-card-primary {
  border-left: 4px solid #667eea;
}

.stats-card-success {
  border-left: 4px solid #28a745;
}

.stats-card-info {
  border-left: 4px solid #17a2b8;
}

.stats-card-warning {
  border-left: 4px solid #ffc107;
}

.stats-card-orange {
  border-left: 4px solid #fd7e14;
}

.stats-card-secondary {
  border-left: 4px solid #6c757d;
}

/* Énfasis especial para tarjeta ACTIVOS */
.stats-card-featured {
  border-left: 5px solid #28a745 !important;
  box-shadow: 0 0.25rem 0.75rem rgba(40, 167, 69, 0.15) !important;
  position: relative;
  overflow: visible;
}

.stats-card-featured::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border: 2px solid rgba(40, 167, 69, 0.2);
  border-radius: 0.75rem;
  pointer-events: none;
}

.stats-card-featured:hover {
  box-shadow: 0 0.5rem 1.5rem rgba(40, 167, 69, 0.25) !important;
  border-left-color: #28a745 !important;
}

.icon-shape-featured {
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

/* Iconos circulares */
.icon-shape {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.icon-shape i {
  color: white;
}

/* Cards de estado y perfil */
.estado-card,
.perfil-card {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.estado-card:hover,
.perfil-card:hover {
  background: #e9ecef;
  transform: translateX(4px);
}

/* Boxes de métricas */
.metric-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  text-align: center;
  transition: all 0.3s ease;
  overflow: hidden; /* Evita desbordamiento */
}

.metric-box:hover {
  background: #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.metric-box h3,
.metric-box h4,
.metric-box h5 {
  font-size: clamp(0.7rem, 1.6vw, 1.5rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  line-height: 1.2;
  max-width: 100%;
}

/* Cards de credenciales */
.credential-card {
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  height: 100%;
}

.credential-card:hover {
  background: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.credential-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

/* Progress bars */
.progress {
  background-color: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
  border-radius: 10px;
}

/* Card headers */
.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border-bottom: 2px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.card-header h5 {
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1rem;
}

/* General card styles */
.card {
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  border: none;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}

/* Badge styles */
.badge {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  font-weight: 500;
}

.badge-orange {
  background-color: #fd7e14;
  color: #fff;
}

/* Typography */
h2 {
  font-size: 2rem;
}

h4 {
  font-size: 1.5rem;
}

h5 {
  font-size: 1.125rem;
}

h6 {
  font-size: 0.875rem;
}

/* Métricas de valorización */
.valorizacion-metric {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  overflow: hidden; /* Evita desbordamiento */
}

.valorizacion-metric:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.valorizacion-metric i {
  font-size: 2rem;
  flex-shrink: 0;
}

.valorizacion-metric h4 {
  font-size: clamp(0.7rem, 1.6vw, 1.5rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  line-height: 1.2;
  max-width: 100%;
}

.valorizacion-metric div {
  min-width: 0; /* Permite que el contenido se ajuste */
  overflow: hidden; /* Evita desbordamiento */
}

/* Tabla de valorización */
.table {
  font-size: 0.875rem;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 1rem 0.75rem;
}

.table tbody td {
  vertical-align: middle;
  padding: 1rem 0.75rem;
}

.valorizacion-row {
  transition: all 0.3s ease;
  cursor: pointer;
}

.valorizacion-row:hover {
  background-color: #f8f9fa;
  transform: translateX(4px);
}

.plan-id-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Card footer */
.card-footer {
  background-color: #f8f9fa !important;
  padding: 1rem 1.5rem;
}

/* Filtros */
.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
  font-size: 0.8125rem;
}

.form-control-sm {
  border: 2px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.form-control-sm:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.1);
}

.bg-light {
  background-color: #f8f9fa !important;
}

/* KPI Cards para Ventas */
.kpi-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: #fff;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
  overflow: hidden; /* Evita desbordamiento */
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15);
}

.kpi-card small {
  font-size: clamp(0.65rem, 1.3vw, 0.875rem);
  white-space: nowrap;
  display: block;
  overflow: hidden;
  max-width: 100%;
}

.kpi-card-primary {
  border-left: 4px solid #667eea;
}

.kpi-card-success {
  border-left: 4px solid #28a745;
}

.kpi-card-danger {
  border-left: 4px solid #dc3545;
}

.kpi-card-info {
  border-left: 4px solid #17a2b8;
}

.kpi-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  flex-shrink: 0;
  margin-right: 1rem;
}

.kpi-icon i {
  color: white;
  font-size: 1.5rem;
}

.kpi-content {
  flex-grow: 1;
  min-width: 0; /* Permite que el contenido se ajuste */
  overflow: hidden; /* Evita desbordamiento */
}

.kpi-content h3 {
  margin-bottom: 0.25rem;
  font-size: clamp(0.7rem, 1.5vw, 1.75rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  line-height: 1.2;
  max-width: 100%;
}

/* Clase específica para valores monetarios largos */
.kpi-content h3.text-success,
.kpi-content h3.text-warning,
.kpi-content h3.text-danger,
.kpi-content h3.text-info,
.kpi-content h3.text-primary {
  font-size: clamp(0.65rem, 1.3vw, 1.75rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  max-width: 100%;
}

/* Distribution Cards */
.distribution-card {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.75rem;
  height: 100%;
  overflow: hidden; /* Evita desbordamiento */
}

.distribution-item {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
  overflow: hidden; /* Evita desbordamiento */
}

.distribution-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.distribution-item .font-weight-bold {
  font-size: clamp(0.65rem, 1.4vw, 1rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  display: inline-block;
  max-width: 100%;
}

/* Purple color */
.bg-purple {
  background-color: #6f42c1 !important;
}

/* Orange color */
.text-orange {
  color: #fd7e14 !important;
}

.bg-orange {
  background-color: #fd7e14 !important;
}

/* Additional Metrics Box */
.additional-metrics-box {
  padding: 1.25rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border-radius: 0.75rem;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.additional-metrics-box:hover {
  border-color: #dee2e6;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.additional-metrics-box h4 {
  font-size: 1.5rem;
  white-space: nowrap;
}

.additional-metrics-box i {
  flex-shrink: 0;
}

/* Gradient primary */
.bg-gradient-primary {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
}

/* Badge más grande para sucursales */
.badge-lg {
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
}

/* Accordion de Sucursales */
.sucursal-accordion-card {
  border: 2px solid #e9ecef;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sucursal-accordion-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.sucursal-accordion-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  padding: 1rem 1.5rem;
  border: none;
  transition: all 0.3s ease;
}

.sucursal-accordion-header:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
}

.transition-icon {
  transition: transform 0.3s ease;
  color: #667eea;
  font-size: 1rem;
}

.collapse {
  transition: height 0.3s ease;
}

/* KPI Boxes de Sucursales */
.sucursal-kpi-box {
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  text-align: center;
  transition: all 0.3s ease;
}

.sucursal-kpi-box:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.sucursal-kpi-box h5 {
  font-size: 1.25rem;
  white-space: nowrap;
}

/* Énfasis especial para KPI ACTIVOS en sucursales */
.sucursal-kpi-featured {
  background: linear-gradient(135deg, #d4edda 0%, #f8f9fa 100%) !important;
  border: 2px solid rgba(40, 167, 69, 0.3);
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
}

.sucursal-kpi-featured:hover {
  background: linear-gradient(135deg, #c3e6cb 0%, #e9ecef 100%) !important;
  border-color: rgba(40, 167, 69, 0.5);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.2);
  transform: translateY(-3px);
}

/* Box de pendientes en sucursales */
.sucursal-pendientes-box {
  padding: 1rem;
  background: #fff;
  border-radius: 0.5rem;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.sucursal-pendientes-box:hover {
  border-color: #ffc107;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.1);
}

/* Tramo de Sucursal */
.tramo-sucursal-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 2px solid #e9ecef;
}

.tramo-badge-sm {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.8rem;
}

.proximo-tramo-sm-box {
  padding: 1rem;
  background: #f0fdf4;
  border-radius: 0.5rem;
  border-left: 3px solid #28a745;
  text-align: center;
}

.proximo-tramo-sm-box h5 {
  font-size: 1.5rem;
  white-space: nowrap;
}

/* Estilos para Métricas de Cierres */
.metric-box-cierre {
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  text-align: center;
  transition: all 0.3s ease;
  height: 100%;
  overflow: hidden; /* Evita desbordamiento */
}

.metric-box-cierre:hover {
  background: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.metric-box-cierre i {
  font-size: 1.5rem;
}

.metric-box-cierre h4 {
  font-size: clamp(0.7rem, 1.6vw, 1.5rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  line-height: 1.2;
  max-width: 100%;
}

.sobrante-faltante-box {
  padding: 1rem;
  background: #fff;
  border-radius: 0.5rem;
  border-left: 4px solid #e9ecef;
  transition: all 0.3s ease;
  overflow: hidden; /* Evita desbordamiento */
}

.sobrante-faltante-box:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transform: translateX(4px);
}

.sobrante-faltante-box strong {
  font-size: clamp(0.65rem, 1.4vw, 1.1rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  display: inline-block;
  max-width: 100%;
}

.sucursal-ranking-card {
  padding: 1.25rem;
  background: #fff;
  border-radius: 0.75rem;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
  height: 100%;
  overflow: hidden; /* Evita desbordamiento */
}

.sucursal-ranking-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  transform: translateY(-2px);
}

.sucursal-ranking-card strong {
  font-size: clamp(0.6rem, 1.2vw, 1rem);
  white-space: nowrap; /* NUNCA partir en múltiples líneas */
  display: inline-block;
  max-width: 100%;
}

.ranking-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 768px) {
  h2 {
    font-size: 1.5rem;
  }
  
  h3 {
    font-size: 1.25rem;
  }
  
  h4 {
    font-size: 1.125rem;
  }
  
  .icon-shape {
    width: 50px;
    height: 50px;
  }
  
  .icon-shape i {
    font-size: 1.5rem !important;
  }
  
  .credential-icon {
    width: 35px;
    height: 35px;
  }
  
  .credential-card,
  .estado-card,
  .perfil-card,
  .metric-box {
    padding: 0.75rem;
  }
  
  .table {
    font-size: 0.75rem;
  }
  
  .plan-id-badge {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }
  
  .valorizacion-metric {
    flex-direction: column;
    text-align: center;
  }
  
  .valorizacion-metric i {
    margin-bottom: 0.5rem;
  }
  
  .kpi-card {
    padding: 1rem;
  }
  
  .kpi-icon {
    width: 50px;
    height: 50px;
    margin-right: 0.75rem;
  }
  
  .kpi-icon i {
    font-size: 1.25rem;
  }
  
  .kpi-content h3 {
    font-size: clamp(0.6rem, 2.5vw, 1.25rem) !important;
    white-space: nowrap !important;
  }
  
  .stats-card h2 {
    font-size: clamp(0.65rem, 3vw, 1.5rem) !important;
    white-space: nowrap !important;
  }
  
  .metric-box h3,
  .metric-box h4,
  .metric-box h5 {
    font-size: clamp(0.6rem, 2.5vw, 1.25rem) !important;
    white-space: nowrap !important;
  }
  
  .valorizacion-metric h4 {
    font-size: clamp(0.6rem, 2.5vw, 1.25rem) !important;
    white-space: nowrap !important;
  }
  
  .distribution-card {
    padding: 1rem;
  }
  
  .distribution-item {
    font-size: 0.875rem;
  }
  
  .metric-box-cierre {
    padding: 1rem;
  }
  
  .metric-box-cierre i {
    font-size: 1.25rem;
  }
  
  .metric-box-cierre h4 {
    font-size: clamp(0.6rem, 2.5vw, 1.25rem) !important;
    white-space: nowrap !important;
  }
  
  .sobrante-faltante-box {
    padding: 0.75rem;
    font-size: 0.875rem;
  }
  
  .sobrante-faltante-box strong {
    font-size: clamp(0.55rem, 2vw, 1rem) !important;
    white-space: nowrap !important;
  }
  
  .sucursal-ranking-card {
    padding: 1rem;
    font-size: 0.875rem;
  }
  
  .sucursal-ranking-card strong {
    font-size: clamp(0.5rem, 1.8vw, 0.9rem) !important;
    white-space: nowrap !important;
  }
  
  .ranking-badge {
    width: 25px;
    height: 25px;
    font-size: 0.75rem;
  }
  
  /* Ajuste de valores monetarios en distribuciones móvil */
  .distribution-item .text-success,
  .distribution-item .text-danger,
  .distribution-item .text-info,
  .distribution-item .text-warning {
    font-size: clamp(0.55rem, 1.8vw, 0.95rem) !important;
    white-space: nowrap !important;
    display: inline-block;
    max-width: 100%;
  }
  
  .distribution-item .text-right {
    min-width: 0;
    overflow: hidden;
  }
  
  .distribution-item .badge {
    font-size: clamp(0.6rem, 1.5vw, 0.875rem) !important;
  }
}
</style>

